import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtEur } from "../lib/api";
import { MODULES } from "../config/modules";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { CalendarCheck, UserPlus, Trash2, Check, X, LogIn } from "lucide-react";
import { IdBadge } from "../components/IdBadge";

const STATO = { in_attesa: { l: "In attesa", c: "bg-amber-100 text-amber-800" }, entrato: { l: "Entrato", c: "bg-emerald-100 text-emerald-700" }, rinuncia: { l: "Rinuncia · trattenuta", c: "bg-red-100 text-red-700" } };

export default function Prenotazioni() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { structures } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [confirmP, setConfirmP] = useState(null);
  const [confBusy, setConfBusy] = useState(false);
  const [renP, setRenP] = useState(null);
  const [renBusy, setRenBusy] = useState(false);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const structName = (id) => structures.find((s) => s.id === id)?.nome || "—";

  const { data = [] } = useQuery({ queryKey: ["prenotazioni"], queryFn: async () => (await api.get("/prenotazioni")).data });
  const inAttesa = data.filter((p) => p.stato === "in_attesa");
  const rinunce = data.filter((p) => p.stato === "rinuncia");
  const totCaparre = inAttesa.reduce((s, p) => s + (Number(p.importo_caparra) || 0), 0);
  const totTratt = rinunce.reduce((s, p) => s + (Number(p.caparra_trattenuta ?? p.importo_caparra) || 0), 0);

  const fields = MODULES.ospiti.fields.filter((f) => !f.createOnly && f.key !== "stato" && f.type !== "operatore_responsabile");

  const save = async () => {
    if (!form.structure_id || !form.cognome) { toast.error("Struttura e Cognome obbligatori"); return; }
    try { await api.post("/prenotazioni", { ...form, importo_caparra: Number(form.importo_caparra) || 0 }); toast.success("Prenotazione creata"); setOpen(false); setForm({}); qc.invalidateQueries({ queryKey: ["prenotazioni"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doConfirm = async () => {
    const { p, data_ingresso, retta } = confirmP;
    if (!data_ingresso || !retta) { toast.error("Inserisci data di ingresso e retta"); return; }
    if (confBusy) return;
    setConfBusy(true);
    try { const r = await api.post(`/prenotazioni/${p.id}/conferma`, { data_ingresso, retta: Number(retta) }); toast.success("Ingresso confermato"); setConfirmP(null); qc.invalidateQueries({ queryKey: ["prenotazioni"] }); navigate(`/ospiti/${r.data.ospite_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setConfBusy(false); }
  };
  const doRinuncia = async () => {
    const { p, mese } = renP;
    if (!mese || mese.length !== 7) { toast.error("Mese non valido (YYYY-MM)"); return; }
    if (renBusy) return;
    setRenBusy(true);
    try { await api.post(`/prenotazioni/${p.id}/rinuncia`, { mese }); toast.success("Rinuncia registrata"); setRenP(null); qc.invalidateQueries({ queryKey: ["prenotazioni"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setRenBusy(false); }
  };
  const del = async (p) => { if (!window.confirm("Eliminare la prenotazione?")) return; await api.delete(`/prenotazioni/${p.id}`); qc.invalidateQueries({ queryKey: ["prenotazioni"] }); };

  const curMonth = new Date().toISOString().slice(0, 7);

  return (
    <div className="space-y-6" data-testid="prenotazioni-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><CalendarCheck size={22} /> Prenotazioni / Caparre</h1>
          <p className="text-sm text-muted-foreground">Persone con caparra versata, in attesa di ingresso. Non contano in occupazione/rette finché non entrano.</p>
        </div>
        <Button onClick={() => { setForm({}); setOpen(true); }} data-testid="new-prenotazione-btn"><UserPlus size={16} className="mr-1" /> Nuova prenotazione</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="gr-card p-3 border-l-4" style={{ borderColor: "#b45309" }}><div className="text-[10px] font-bold uppercase text-muted-foreground">In attesa di ingresso</div><div className="text-2xl font-head font-bold" style={{ color: "#b45309" }}>{inAttesa.length}</div></div>
        <div className="gr-card p-3 border-l-4" style={{ borderColor: "#0369a1" }}><div className="text-[10px] font-bold uppercase text-muted-foreground">Caparre incassate (deposito)</div><div className="text-2xl font-head font-bold" style={{ color: "#0369a1" }}>{fmtEur(totCaparre)}</div></div>
        <div className="gr-card p-3 border-l-4" style={{ borderColor: "#b91c1c" }}><div className="text-[10px] font-bold uppercase text-muted-foreground">Rinunce (caparra trattenuta)</div><div className="text-2xl font-head font-bold" style={{ color: "#b91c1c" }}>{fmtEur(totTratt)}</div></div>
      </div>

      <div className="gr-card p-4">
        <h2 className="font-bold mb-3">In attesa</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b-2">
              <th className="p-2">Nominativo</th><th className="p-2">Struttura</th><th className="p-2">Ingresso previsto</th><th className="p-2">Caparra</th><th className="p-2">Data caparra</th><th className="p-2 text-right">Azioni</th>
            </tr></thead>
            <tbody>
              {inAttesa.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Nessuna prenotazione in attesa.</td></tr>}
              {inAttesa.map((p) => (
                <tr key={p.id} className="border-b" data-testid={`pren-row-${p.id}`}>
                  <td className="p-2 font-semibold">{[p.cognome, p.nome].filter(Boolean).join(" ") || "—"}
                    <span className="block text-[10px] font-mono font-normal text-muted-foreground mt-0.5" data-testid={`pren-code-${p.id}`} title="Codice prenotazione — la scheda ospite (con ID) verrà creata alla conferma dell'ingresso">Prenotazione #{String(p.id).slice(-6)}</span>
                  </td>
                  <td className="p-2">{structName(p.structure_id)}</td>
                  <td className="p-2">{p.data_ingresso_prevista ? fmtDate(p.data_ingresso_prevista) : "—"}</td>
                  <td className="p-2 font-semibold">{fmtEur(p.importo_caparra || 0)}</td>
                  <td className="p-2 text-muted-foreground">{p.data_caparra ? fmtDate(p.data_caparra) : "—"}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button size="sm" className="mr-1" onClick={() => setConfirmP({ p, data_ingresso: p.data_ingresso_prevista || "", retta: "" })} data-testid={`conferma-${p.id}`}><Check size={14} className="mr-1" />Conferma ingresso</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 mr-1" onClick={() => setRenP({ p, mese: curMonth })} data-testid={`rinuncia-${p.id}`}><X size={14} className="mr-1" />Rinuncia</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p)}><Trash2 size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {rinunce.length > 0 && (
        <div className="gr-card p-4 bg-red-50/40">
          <h2 className="font-bold mb-1 text-red-800">Caparre trattenute (rinunce)</h2>
          <p className="text-xs text-muted-foreground mb-3">Chi ha cambiato idea ma la caparra resta alla struttura → ricavo nel mese indicato.</p>
          <table className="w-full text-sm">
            <tbody>
              {rinunce.map((p) => (
                <tr key={p.id} className="border-b" data-testid={`rinuncia-row-${p.id}`}>
                  <td className="p-2 font-semibold">{[p.cognome, p.nome].filter(Boolean).join(" ") || "—"}
                    {p.ospite_id
                      ? <IdBadge id={p.ospite_id} className="block mt-0.5 text-[10px]" />
                      : <span className="block text-[10px] font-mono font-normal text-muted-foreground mt-0.5">Prenotazione #{String(p.id).slice(-6)}</span>}
                  </td>
                  <td className="p-2">{structName(p.structure_id)}</td>
                  <td className="p-2">Caparra trattenuta: <b>{fmtEur(p.caparra_trattenuta ?? p.importo_caparra ?? 0)}</b></td>
                  <td className="p-2 text-muted-foreground">Mese: {p.rinuncia_mese || "—"}</td>
                  <td className="p-2"><span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">Rinuncia · trattenuta</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form nuova prenotazione (stessi campi ospite + caparra) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova prenotazione</DialogTitle><DialogDescription>Stessi dati di una scheda ospite + la caparra. Alla conferma ingresso compilerà la scheda identica.</DialogDescription></DialogHeader>
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 mb-2 grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs font-semibold mb-1 block">Importo caparra (€)</Label><Input type="number" value={form.importo_caparra || ""} onChange={(e) => setF("importo_caparra", e.target.value)} data-testid="pren-caparra" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Data caparra</Label><Input type="date" value={form.data_caparra || ""} onChange={(e) => setF("data_caparra", e.target.value)} data-testid="pren-data-caparra" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Ingresso previsto</Label><Input type="date" value={form.data_ingresso_prevista || ""} onChange={(e) => setF("data_ingresso_prevista", e.target.value)} data-testid="pren-ingresso-prev" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => {
              if (f.type === "struttura") return (
                <div key="struttura"><Label className="text-xs font-semibold mb-1 block">Struttura *</Label>
                  <Select value={form.structure_id || ""} onValueChange={(v) => setF("structure_id", v)}>
                    <SelectTrigger data-testid="pren-struttura"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              );
              if (f.type === "select") return (
                <div key={f.key}><Label className="text-xs font-semibold mb-1 block">{f.label}</Label>
                  <Select value={form[f.key] || ""} onValueChange={(v) => setF(f.key, v)}>
                    <SelectTrigger data-testid={`pren-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{(f.options || []).map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              );
              if (f.type === "textarea") return (
                <div key={f.key} className="sm:col-span-2"><Label className="text-xs font-semibold mb-1 block">{f.label}</Label><Textarea value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`pren-${f.key}`} /></div>
              );
              return (
                <div key={f.key}><Label className="text-xs font-semibold mb-1 block">{f.label}{f.req ? " *" : ""}</Label>
                  <Input type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`pren-${f.key}`} /></div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="pren-save">Salva prenotazione</Button></div>
        </DialogContent>
      </Dialog>

      {/* Conferma ingresso */}
      <Dialog open={!!confirmP} onOpenChange={() => setConfirmP(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LogIn size={18} />Conferma ingresso</DialogTitle>
            <DialogDescription>{confirmP && `${[confirmP.p.cognome, confirmP.p.nome].filter(Boolean).join(" ")} · caparra ${fmtEur(confirmP.p.importo_caparra || 0)} (verrà scalata dalla prima retta).`}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">Data di ingresso *</Label><Input type="date" value={confirmP?.data_ingresso || ""} onChange={(e) => setConfirmP((s) => ({ ...s, data_ingresso: e.target.value }))} data-testid="conferma-data" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Retta mensile stabilita (€) *</Label><Input type="number" value={confirmP?.retta || ""} onChange={(e) => setConfirmP((s) => ({ ...s, retta: e.target.value }))} data-testid="conferma-retta" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setConfirmP(null)}>Annulla</Button><Button onClick={doConfirm} disabled={confBusy} data-testid="conferma-ok">{confBusy ? "Conferma…" : "Conferma ingresso"}</Button></div>
        </DialogContent>
      </Dialog>

      {/* Rinuncia */}
      <Dialog open={!!renP} onOpenChange={() => setRenP(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-700">Rinuncia · caparra trattenuta</DialogTitle>
            <DialogDescription>{renP && `La caparra di ${fmtEur(renP.p.importo_caparra || 0)} resta alla struttura e viene registrata come ricavo nel mese indicato. L'ospite comparirà solo in quel mese.`}</DialogDescription></DialogHeader>
          <div><Label className="text-xs font-semibold mb-1 block">Mese di competenza (YYYY-MM) *</Label><Input value={renP?.mese || ""} onChange={(e) => setRenP((s) => ({ ...s, mese: e.target.value }))} placeholder="2026-10" data-testid="rinuncia-mese" /></div>
          <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setRenP(null)}>Annulla</Button><Button className="bg-red-600 hover:bg-red-700" onClick={doRinuncia} disabled={renBusy} data-testid="rinuncia-ok">{renBusy ? "Registrazione…" : "Registra rinuncia"}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
